import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { postingEmail } from './NodeMailer';
import { useStateContext } from '../context/StateContext';
import { positionNumber } from '../lib/utils';

function ErrorFallback({error, resetErrorBoundary}) {
  const [isSending, setIsSending] = useState(false);
  
  // Try to get context, but don't fail if it's not available
  let contextValues = {};
  try {
    contextValues = useStateContext() || {};
  } catch (e) {
    console.log('Context not available:', e);
  }

  const {
    client,
    register,
    site,
    sUser,
    customer,
    currentDevice
  } = contextValues;

  // Function to clean stack trace for better readability while preserving source info
  const cleanStackTrace = (stack) => {
    if (!stack) return '';
    
    // Split stack into lines
    const lines = stack.split('\n');
    
    // Process each line to make it more readable while preserving source info
    return lines
      .map?.(line => {
        // Keep the original line for source-mapped traces
        if (line.includes('at ') && !line.includes('webpack-internal:')) {
          return line.trim();
        }

        // For webpack internal paths, clean them up but preserve file names and line numbers
        line = line.replace(/webpack-internal:\/\/\/(\.\/)?/g, '');
        // Preserve the actual component/file name and line numbers
        const match = line.match(/(?:at\s+)?([^(\s]+)?\s*(?:\((.*?):(\d+):(\d+)\))?/);
        if (match) {
          const [_, fnName, file, line, col] = match;
          if (fnName && file) {
            return `    at ${fnName} (${file}:${line}:${col})`.trim();
          }
        }
        return line.trim();
      })
      .filter?.(Boolean) // Remove empty lines
      .join('\n');
  };

  // Function to get a more detailed error message
  const getDetailedError = (error) => {
    if (!error) return 'Unknown error';

    let details = [];
    
    // Add the error name if it exists
    if (error.name && error.name !== 'Error') {
      details.push(`${error.name}`);
    }

    // Add the error message
    if (error.message) {
      details.push(error.message);
    }

    // Add any additional properties that might be useful
    const additionalProps = ['code', 'type', 'status', 'statusText']
      .filter?.(prop => error[prop])
      .map?.(prop => `${prop}: ${error[prop]}`);
    
    if (additionalProps.length > 0) {
      details.push(...additionalProps);
    }

    return details.join('\n');
  };

  const sendErrorReport = async () => {
    if (isSending) return;
    setIsSending(true);
    
    // Show loading state with Swal
    Swal.fire({
      title: 'Invio in corso...',
      text: 'Invio del report di errore in corso',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    
    // Basic error details that don't require context
    const errorDetails = {
      message: getDetailedError(error),
      stack: cleanStackTrace(error?.stack),
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Add context-dependent details only if available
    if (sUser) errorDetails.user = sUser?.firstName + " " + sUser?.lastName;
    if (customer?.label) errorDetails.customer = customer.label;
    if (client?.label) errorDetails.client = client.label;
    if (site?.label) errorDetails.site = site.label;
    if (register?.family && register?.name) {
      errorDetails.register = register.family + " " + register.name;
    }
    if (currentDevice?.label) errorDetails.device = currentDevice.label;
    
    const devicePosition = positionNumber(currentDevice);
    if (devicePosition) errorDetails.devicePosition = devicePosition;

    // Add IDs if available
    if (client?._id) errorDetails.clientId = client._id;
    if (register?._id) errorDetails.registerId = register._id;
    if (site?._id) errorDetails.siteId = site._id;
    if (customer?._id) errorDetails.customerId = customer._id;
    if (currentDevice?._id) errorDetails.deviceId = currentDevice._id;
    if (sUser?._id) errorDetails.userId = sUser._id;

    // Create text content with all details
    const textContent = `
MarginFM Application Error Report

Error Details:
${errorDetails.message ? `Message: ${errorDetails.message}` : ''}
Timestamp: ${errorDetails.timestamp}
URL: ${errorDetails.url}
User Agent: ${errorDetails.userAgent}

Context Information:
${errorDetails.user ? `User: ${errorDetails.user}` : ''}
${errorDetails.customer ? `Customer: ${errorDetails.customer}` : ''}
${errorDetails.client ? `Client: ${errorDetails.client}` : ''}
${errorDetails.site ? `Site: ${errorDetails.site}` : ''}
${errorDetails.register ? `Register: ${errorDetails.register}` : ''}
${errorDetails.device ? `Device: ${errorDetails.device}` : ''}
${errorDetails.devicePosition ? `Device Position: ${errorDetails.devicePosition}` : ''}

System IDs:
${errorDetails.userId ? `User ID: ${errorDetails.userId}` : ''}
${errorDetails.customerId ? `Customer ID: ${errorDetails.customerId}` : ''}
${errorDetails.clientId ? `Client ID: ${errorDetails.clientId}` : ''}
${errorDetails.siteId ? `Site ID: ${errorDetails.siteId}` : ''}
${errorDetails.registerId ? `Register ID: ${errorDetails.registerId}` : ''}
${errorDetails.deviceId ? `Device ID: ${errorDetails.deviceId}` : ''}

${errorDetails.stack ? `Stack Trace:\n${errorDetails.stack}` : ''}`;

    // Format HTML content for email with all details
    const htmlContent = `
      <h2>relyOnFm Application Error Report</h2>
      
      <h3>Error Details:</h3>
      ${errorDetails.message ? `<p><strong>Message:</strong> <pre style="white-space: pre-wrap;">${errorDetails.message}</pre></p>` : ''}
      <p><strong>Timestamp:</strong> ${errorDetails.timestamp}</p>
      <p><strong>URL:</strong> ${errorDetails.url}</p>
      <p><strong>User Agent:</strong> ${errorDetails.userAgent}</p>
            
      ${errorDetails.stack ? `
        <h3>Stack Trace:</h3>
        <pre style="background-color: #f5f5f5; padding: 10px; border-radius: 5px; white-space: pre-wrap; font-family: monospace;">
          ${errorDetails.stack}
        </pre>
      ` : ''}

      <h3>Context Information:</h3>
      ${errorDetails.user ? `<p><strong>User:</strong> ${errorDetails.user}</p>` : ''}
      ${errorDetails.customer ? `<p><strong>Customer:</strong> ${errorDetails.customer}</p>` : ''}
      ${errorDetails.client ? `<p><strong>Client:</strong> ${errorDetails.client}</p>` : ''}
      ${errorDetails.site ? `<p><strong>Site:</strong> ${errorDetails.site}</p>` : ''}
      ${errorDetails.register ? `<p><strong>Register:</strong> ${errorDetails.register}</p>` : ''}
      ${errorDetails.device ? `<p><strong>Device:</strong> ${errorDetails.device}</p>` : ''}
      ${errorDetails.devicePosition ? `<p><strong>Device Position:</strong> ${errorDetails.devicePosition}</p>` : ''}
      
      <h3>System IDs:</h3>
      ${errorDetails.userId ? `<p><strong>User ID:</strong> ${errorDetails.userId}</p>` : ''}
      ${errorDetails.customerId ? `<p><strong>Customer ID:</strong> ${errorDetails.customerId}</p>` : ''}
      ${errorDetails.clientId ? `<p><strong>Client ID:</strong> ${errorDetails.clientId}</p>` : ''}
      ${errorDetails.siteId ? `<p><strong>Site ID:</strong> ${errorDetails.siteId}</p>` : ''}
      ${errorDetails.registerId ? `<p><strong>Register ID:</strong> ${errorDetails.registerId}</p>` : ''}
      ${errorDetails.deviceId ? `<p><strong>Device ID:</strong> ${errorDetails.deviceId}</p>` : ''}
    `;

    try {
      // Send email with result callback
      await new Promise((resolve, reject) => {
        postingEmail({
          subject: 'MarginFM Application Error Report',
          toEmail: 'support@relyonfm.com',
          text: textContent,
          html: htmlContent
        }, (result) => {
          if (result?.Success === "SUCCESS") {
            console.log("ERROR EMAIL SENT SUCCESSFULLY");
            resolve();
          } else {
            reject(new Error('Failed to send email'));
          }
        });
      });

      // Show success message
      await Swal.fire({
        title: 'Successo!',
        text: 'Report di errore inviato con successo',
        icon: 'success',
        timer: 3000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Failed to send error report:', err);
      
      // Show error message and ask for fallback
      const result = await Swal.fire({
        title: 'Errore',
        text: 'Impossibile inviare il report. Vuoi utilizzare il client email predefinito?',
        icon: 'error',
        showCancelButton: true,
        confirmButtonText: 'Usa client email',
        cancelButtonText: 'Annulla'
      });

      if (result.isConfirmed) {
        // Fall back to mailto
        const mailtoLink = `mailto:info@relyonfm.com?subject=Error Report&body=${encodeURIComponent(textContent)}`;
        window.location.href = mailtoLink;
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div style={{marginTop:"50px"}} className="products-heading" role="alert">
      <h2>Si è verificato un errore:</h2>
      <pre style={{ 
        backgroundColor: '#fff3f3', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px',
        whiteSpace: 'pre-wrap',
        fontFamily: 'monospace',
        fontSize: '0.9em'
      }}>
        {getDetailedError(error)}
      </pre>
      <div style={{ 
        backgroundColor: '#fff3f3', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <div style={{ fontSize: '0.9em' }}>
          {sUser && <p><strong>Utente:</strong> {sUser?.firstName} {sUser?.lastName}</p>}
          {customer?.label && <p><strong>Manutentore:</strong> {customer.label}</p>}
          {client?.label && <p><strong>Cliente Finale:</strong> {client.label}</p>}
          {site?.label && <p><strong>Sito:</strong> {site.label}</p>}
          {register?.family || register?.name && (
            <p><strong>Registro:</strong> {register?.family} {register?.name}</p>
          )}
          {currentDevice?.label && (
            <p><strong>Dispositivo:</strong> {currentDevice.label}</p>
          )}
          {currentDevice && positionNumber(currentDevice) && (
            <p><strong>Posizione Dispositivo:</strong> {positionNumber(currentDevice)}</p>
          )}
          <p><strong>URL:</strong> {window.location.href}</p>
          <p><strong>Data e Ora:</strong> {new Date().toLocaleString('it-IT')}</p>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
        <button
          onClick={sendErrorReport}
          className='pdf-button'
          disabled={isSending}
          style={{
            backgroundColor: '#4CAF50',
            cursor: isSending ? 'not-allowed' : 'pointer',
            opacity: isSending ? 0.7 : 1
          }}
        >
          {isSending ? 'Spedisco...' : 'Spedizione Report Errore'}
        </button>
        <button
          onClick={() => {
            window.location.reload();
          }}
          className="pdf-button"
        >
          Ricarica Pagina
        </button>
      </div>
    </div>
  );
}

export default ErrorFallback;
